import { getColumnRootX } from '../../../helpers/gridBoundariesHelpers';
import { OnResizerMouseDown } from '../../../hooks/useGridResizer';
import { UseSheetTheme } from '../../../hooks/useSheetTheme';
import {
  MdOutlineKeyboardArrowDown,
  MdOutlineKeyboardArrowUp,
} from 'react-icons/md';
import { SheetDefaults } from '../../../constants/sheetDefaults';
import { Flex } from '@chakra-ui/react';
import { IconType } from 'react-icons/lib';
import { GridEntity, IRange } from '../../../types';
import { SheetCommandType } from '../../../types/commands/sheetCommands/sheetCommands';
import { useAppDispatch } from '../../../../core/hooks/storeHooks';
import { handleSheetOperations } from '../../../redux/sagas/root/rootActions';
import { RowCellView } from '../../../helpers/cellView/cellViewTypes';

interface RowResizerProps
  extends Pick<UseSheetTheme, 'selectionBorder' | 'stroke' | 'isRtl'>,
    RowCellView {
  sheetId: string;
  linePos: number;
  gridX: number;
  gridWidth: number;
  gridY: number;
  gridHeight: number;
  rowHeaderWidth: number;
  lineSize: number;
  zoom: number;

  onResizerMouseDown: OnResizerMouseDown;
}
const RowResizer: React.FC<RowResizerProps> = ({
  sheetId,

  ri: rowIndex,
  linePos,
  gridX,
  gridY,
  gridWidth,
  gridHeight,
  rowHeaderWidth,
  lineSize,
  selectionBorder,
  stroke,

  y,
  height,
  isRtl,
  zoom,

  hiddenRows,

  onResizerMouseDown,
}) => {
  const {
    hiddenRange: hiddenRowRange,
    previousHiddenRange: previousHiddenRowRange,
    nextHiddenRange: nextHiddenRowRange,
  } = hiddenRows ?? {};

  
  const dispatch = useAppDispatch();
  const unHideIconSize = SheetDefaults.unHideIconSize * zoom;
  const left = getColumnRootX(gridX, gridWidth, rowHeaderWidth, isRtl);
  const lineHalf = lineSize / 2;
  const startMouseDown = (e: React.MouseEvent) =>
    onResizerMouseDown(
      e,
      y - lineHalf, // Both min and max are relative position therfore we don't need to add windowY
      gridY + gridHeight - lineSize,
      linePos,
      rowIndex,
      'row'
    );

  const iconLeft = isRtl ? left + rowHeaderWidth - unHideIconSize : left + 2;

  let hiddenContainerHeight = unHideIconSize * 2;

  const generalStyles = {
    left: `${iconLeft}px`,
    height: `${unHideIconSize}px`,
    width: `${unHideIconSize}px`,
    background: stroke,
  };

  const directionIcon = (Icon: IconType) => (
    <Flex
      style={{ height: `${unHideIconSize}px`, width: '100%' }}
      align="center"
      justify="center"
    >
      <Icon />
    </Flex>
  );

  const handleUnhide = (range: IRange) =>
    dispatch(
      handleSheetOperations({
        type: SheetCommandType.UnhideGrid,
        ranges: [range],
        sheetId,
        entity: GridEntity.Row,
      })
    );

  // This is 1 single group spanning over to next row
  const unHideButtons = () =>
    hiddenRowRange && (
      <div
        style={{
          ...generalStyles,
          top: `${y + height - unHideIconSize}px`,
          height: `${hiddenContainerHeight}px`,
          flexDirection: 'column',
          borderRadius: '3px',
        }}
        className="unhide-container"
        onClick={() => handleUnhide(hiddenRowRange)}
      >
        {directionIcon(MdOutlineKeyboardArrowUp)}
        {directionIcon(MdOutlineKeyboardArrowDown)}
      </div>
    );

  const unHideTopRange = () =>
    previousHiddenRowRange && (
      <div
        style={{
          top: `${y}px`,
          ...generalStyles,
          borderBottomLeftRadius: '3px',
          borderBottomRightRadius: '3px',
        }}
        className="unhide-container"
        onClick={() => handleUnhide(previousHiddenRowRange)}
      >
        {directionIcon(MdOutlineKeyboardArrowDown)}
      </div>
    );

  const unHideBottomRange = () =>
    nextHiddenRowRange && (
      <div
        style={{
          top: `${y + height - unHideIconSize}px`,
          ...generalStyles,
          borderTopRightRadius: '3px',
          borderTopLeftRadius: '3px',
        }}
        className="unhide-container"
        onClick={() => handleUnhide(nextHiddenRowRange)}
      >
        {directionIcon(MdOutlineKeyboardArrowUp)}
      </div>
    );

  return (
    <div>
      {unHideTopRange()}
      {!hiddenRowRange && unHideBottomRange()}
      {unHideButtons()}
      <div
        style={{
          position: 'absolute',
          top: `${linePos}px`,
          left: `${left}px`,
          height: `${lineSize}px`,
          width: `${rowHeaderWidth}px`,
          backgroundColor: selectionBorder,
          cursor: 'row-resize',
          zIndex: 3,
        }}
        onMouseDown={startMouseDown}
        className="resizer-hidden-line"
      />
    </div>
  );
};

export default RowResizer;
